import styled from 'styled-components';

import { BodySmall, LabelSmallBold } from 'styles/typography';

import DetailSlideout from '../DetailSlideout';

import { ScanCard } from './scanCards';

// What a cell renders when the sweep didn't capture that field. An em dash, never a blank —
// a blank cell reads as a rendering fault rather than as missing data.
const MISSING = '–';

interface Props {
  card: ScanCard | null;
  onClose: () => void;
  // Opens the task the row's referral became. Every row has one: the api writes a workflow id
  // exactly for the `created` disposition, and no other disposition reaches this table.
  onOpenTask: (workflowId: string, siblingWorkflowIds: string[]) => void;
}

/**
 * The results of one scan.
 *
 * THREE columns, not the five in the mock: gender and SSN are punted because the sweep doesn't
 * retrieve them, and a column that can only ever render an em dash is worse than no column.
 */
export default function ScanResultsSlideout({ card, onClose, onOpenTask }: Props) {
  const rows = card?.rows ?? [];
  const siblings = rows.map((row) => row.workflowId);

  return (
    <DetailSlideout
      open={!!card}
      onClose={onClose}
      title={card ? `${card.portalName} scan results` : 'Scan results'}
      subtitle={card ? card.detail : undefined}>
      <Table data-testid='scan-results-table'>
        <thead>
          <tr>
            <Th>Patient Name</Th>
            <Th>Date of Birth</Th>
            <Th>SNF</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} data-testid='scan-result-row'>
              <Td>
                <NameLink
                  type='button'
                  onClick={() => onOpenTask(row.workflowId, siblings)}
                  data-testid='scan-result-name'>
                  {row.patientName || MISSING}
                </NameLink>
              </Td>
              <Td data-testid='scan-result-dob'>{row.patientDob || MISSING}</Td>
              <Td data-testid='scan-result-facility'>{row.facilityName || MISSING}</Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </DetailSlideout>
  );
}

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled(LabelSmallBold).attrs({ as: 'th' })`
  text-align: left;
  padding: 8px 12px;
  color: ${({ theme }) => theme.colors.black75};
  border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
`;

const Td = styled(BodySmall).attrs({ as: 'td' })`
  padding: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
  color: ${({ theme }) => theme.colors.black100};
`;

const NameLink = styled.button`
  font-family: ${({ theme }) => theme.textStyles.base.fontFamily};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.blue100};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
`;
